import React from 'react';
import { Card } from './ui/card';
import { 
  Users, 
  UserCheck, 
  Crown, 
  Ban, 
  UserX, 
  Activity,
  Heart,
  MessageCircle,
  TrendingUp,
  Calendar
} from 'lucide-react';

const AdminStats = ({ stats }) => {
  if (!stats) {
    return (
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {[...Array(8)].map((_, i) => (
          <Card key={i} className="p-6 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
            <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          </Card>
        ))}
      </div>
    );
  }

  const userStats = [
    {
      title: 'Total Users',
      value: stats.users.total,
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    {
      title: 'Active Users',
      value: stats.users.active,
      icon: UserCheck,
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    },
    {
      title: 'Premium Users',
      value: stats.users.premium,
      icon: Crown,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100'
    },
    {
      title: 'Verified Users',
      value: stats.users.verified,
      icon: UserCheck,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100'
    },
    {
      title: 'Suspended Users',
      value: stats.users.suspended,
      icon: UserX,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100'
    },
    {
      title: 'Banned Users',
      value: stats.users.banned,
      icon: Ban,
      color: 'text-red-600',
      bgColor: 'bg-red-100'
    },
    {
      title: 'Recent Signups',
      value: stats.users.recent_registrations,
      icon: Calendar,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-100',
      subtitle: 'Last 7 days'
    }
  ];

  const activityStats = [
    {
      title: 'Total Matches',
      value: stats.activity.total_matches,
      icon: Heart,
      color: 'text-pink-600',
      bgColor: 'bg-pink-100'
    },
    {
      title: 'Total Swipes',
      value: stats.activity.total_swipes,
      icon: Activity,
      color: 'text-cyan-600',
      bgColor: 'bg-cyan-100'
    },
    {
      title: 'Total Messages',
      value: stats.activity.total_messages,
      icon: MessageCircle,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-100'
    }
  ];

  const StatCard = ({ title, value, icon: Icon, color, bgColor, subtitle }) => (
    <Card className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-semibold text-gray-900">{value?.toLocaleString() || 0}</p>
          {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-full ${bgColor}`}>
          <Icon className={`w-6 h-6 ${color}`} />
        </div>
      </div>
    </Card>
  );

  const calculatePercentages = () => {
    const total = stats.users.total;
    if (total === 0) return {};

    return {
      active: ((stats.users.active / total) * 100).toFixed(1),
      premium: ((stats.users.premium / total) * 100).toFixed(1),
      verified: ((stats.users.verified / total) * 100).toFixed(1),
      suspended: ((stats.users.suspended / total) * 100).toFixed(1),
      banned: ((stats.users.banned / total) * 100).toFixed(1)
    };
  };

  const percentages = calculatePercentages();

  return (
    <div className="space-y-8">
      {/* User Statistics */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">User Statistics</h3>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {userStats.map((stat, index) => (
            <StatCard key={index} {...stat} />
          ))}
        </div>
      </div>

      {/* Activity Statistics */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Platform Activity</h3>
        <div className="grid gap-6 md:grid-cols-3">
          {activityStats.map((stat, index) => (
            <StatCard key={index} {...stat} />
          ))}
        </div>
      </div>

      {/* User Status Breakdown */}
      <Card className="p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">User Status Breakdown</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Active Users</span>
            <div className="flex items-center space-x-2">
              <div className="w-32 bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-500 h-2 rounded-full" 
                  style={{ width: `${percentages.active}%` }}
                ></div>
              </div>
              <span className="text-sm font-medium text-gray-900">{percentages.active}%</span>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Premium Users</span>
            <div className="flex items-center space-x-2">
              <div className="w-32 bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-yellow-500 h-2 rounded-full" 
                  style={{ width: `${percentages.premium}%` }}
                ></div>
              </div>
              <span className="text-sm font-medium text-gray-900">{percentages.premium}%</span>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Verified Users</span>
            <div className="flex items-center space-x-2">
              <div className="w-32 bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-purple-500 h-2 rounded-full" 
                  style={{ width: `${percentages.verified}%` }}
                ></div>
              </div>
              <span className="text-sm font-medium text-gray-900">{percentages.verified}%</span>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Suspended Users</span>
            <div className="flex items-center space-x-2">
              <div className="w-32 bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-orange-500 h-2 rounded-full" 
                  style={{ width: `${percentages.suspended}%` }}
                ></div>
              </div>
              <span className="text-sm font-medium text-gray-900">{percentages.suspended}%</span>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Banned Users</span>
            <div className="flex items-center space-x-2">
              <div className="w-32 bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-red-500 h-2 rounded-full" 
                  style={{ width: `${percentages.banned}%` }}
                ></div>
              </div>
              <span className="text-sm font-medium text-gray-900">{percentages.banned}%</span>
            </div>
          </div>
        </div>
      </Card>

      {/* Premium Subscriptions (derived from Stripe state only) */}
      {stats.subscriptions && (
        <Card className="p-6" data-testid="admin-subscriptions-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Premium Subscriptions</h3>
            <span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${stats.subscriptions.stripe_mode === 'live' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`} data-testid="admin-stripe-mode">
              Stripe {stats.subscriptions.stripe_mode.toUpperCase()} mode
            </span>
          </div>
          <div className="grid gap-4 md:grid-cols-5 mb-6">
            {[
              ['Premium subscribers', stats.subscriptions.premium_subscribers, 'text-amber-600'],
              ['Active', stats.subscriptions.active, 'text-green-600'],
              ['Past due', stats.subscriptions.past_due, 'text-orange-600'],
              ['Canceled', stats.subscriptions.canceled, 'text-red-600'],
              ['Cancel scheduled', stats.subscriptions.cancel_scheduled, 'text-gray-600'],
            ].map(([label, value, color]) => (
              <div key={label} className="text-center">
                <div className={`text-2xl font-bold ${color}`}>{value ?? 0}</div>
                <div className="text-sm text-gray-600">{label}</div>
              </div>
            ))}
          </div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Plan distribution (active)</h4>
          <div className="space-y-2" data-testid="admin-plan-distribution">
            {Object.keys(stats.subscriptions.plan_distribution || {}).length === 0 && <p className="text-sm text-gray-500">No active subscriptions yet.</p>}
            {stats.subscriptions.legacy_or_other_total > 0 && <p className="text-xs text-gray-500" data-testid="admin-legacy-subs">Historical legacy/other subscriptions on record (not a current plan): {stats.subscriptions.legacy_or_other_total}</p>}
            {Object.entries(stats.subscriptions.plan_distribution || {}).map(([plan, n]) => (
              <div key={plan} className="flex items-center justify-between text-sm">
                <span className="text-gray-600">{{ premium_monthly: 'Monthly $19.99', premium_3_month: '3-Month $49.99', premium_annual: 'Annual $99.99', legacy_other: 'Legacy / other (not a current plan)' }[plan] || plan}</span>
                <span className="font-medium text-gray-900">{n}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Platform Health */}
      <Card className="p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Platform Health</h3>
        <div className="grid gap-4 md:grid-cols-3">
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {stats.activity.total_matches && stats.users.total ? 
                (stats.activity.total_matches / stats.users.total).toFixed(1) : '0'
              }
            </div>
            <div className="text-sm text-gray-600">Avg Matches per User</div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {stats.activity.total_messages && stats.activity.total_matches ? 
                (stats.activity.total_messages / stats.activity.total_matches).toFixed(1) : '0'
              }
            </div>
            <div className="text-sm text-gray-600">Messages per Match</div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">
              {((stats.users.active / stats.users.total) * 100).toFixed(1)}%
            </div>
            <div className="text-sm text-gray-600">User Retention</div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AdminStats;